import { useState } from "react";
import { validateWithRule } from "./Validations";

type TFieldValidated = {
  [field: string]: {
    valid: boolean;
    text: string;
    hasBeenValidated?: boolean;
  };
};

type TFieldValidation = {
  [field: string]: {} | string | Function;
};

const validFieldObject = { valid: true, text: "" };

function useValidations(validations: TFieldValidation): {
  validated: TFieldValidated;
  validateFields: (data?: any, allFiels?: boolean) => boolean;
  addFieldValidation: Function;
  reset: Function;
  hasErrors: Function;
} {
  /** Objeto com o resultado das validações dos campos */
  const [resultValidations, setResultValidations] = useState(
    createInicialState(validations)
  );
  /** Objeto com as configurações das validações */
  const [fieldsValidations, setFieldsValidations] = useState(validations);

  /** Valida todos os campos passados dentro do objeto data. */
  function validateFields(
    data: { [field: string]: any },
    allFiels: boolean = false
  ): boolean {
    let newValidated = resultValidations;

    const fields = allFiels
      ? Object.keys(resultValidations)
      : Object.keys(data);

    fields.forEach((field) => {
      const result = validate(field, data[field], data);
      newValidated = setFieldValidationResult(newValidated, field, result);
    });
    return allFieldsIsValid(newValidated);
  }

  /** Faz a validação de acordo com a configuração para o campo passado. */
  function validate(name, value, allData?) {
    if (!hasField(name)) return;

    const validations = fieldsValidations[name];
    const types = getValidationsTypes(validations);

    for (const type of types) {
      const result =
        type === "custom"
          ? executeCustomValidation(
              isFunction(validations) ? validations : validations["custom"],
              value,
              allData
            )
          : executeValidations(validations, type, name, value);

      if (!result.valid) return result;
    }

    /** Nenhum erro encontrado para o valor do campo. */
    return;
  }

  /** Obtém quais as validações que serão aplicadas no campo. */
  function getValidationsTypes(validations): string[] {
    if (isFunction(validations)) return ["custom"];

    return typeof validations === "string"
      ? [validations]
      : Object.keys(validations);
  }

  /** Executa a validação customizada que é passada como função. */
  function executeCustomValidation(validation, value, data?) {
    if (!isFunction(validation)) return validFieldObject;
    return validation(value, data) || validFieldObject;
  }

  /** Executa as validações com regras pré-definidas. */
  function executeValidations(validations, type, field, value) {
    const properties = validations[type];
    const result = validateWithRule(type, field, value, properties);
    const error: any = result.error;
    return {
      valid: result.valid,
      text: error?.customMessage,
    };
  }

  /** Atualiza o resultado da validação do campo */
  function setFieldValidationResult(
    fieldsValidated: TFieldValidated,
    name: string,
    object?,
    hasBeenValidated = false
  ): TFieldValidated {
    const newFields = { ...fieldsValidated };
    newFields[name] = object
      ? { ...object, hasBeenValidated }
      : { ...validFieldObject, hasBeenValidated };
    setResultValidations(newFields);

    return newFields;
  }

  /** Adiciona regras de validação para um novo campo */
  function addFieldValidation(
    name: string,
    validation: {} | string | Function
  ) {
    if (!name || !validation || hasField(name)) return;

    const newFields = { ...fieldsValidations };
    newFields[name] = validation;
    setFieldsValidations(newFields);

    setFieldValidationResult(resultValidations, name);
  }

  /** Reseta o objeto com os resultados das validações */
  function reset() {
    const newFields = createInicialState(validations);
    setResultValidations(newFields);
  }

  function hasField(name: string): boolean {
    return fieldsValidations && fieldsValidations[name] !== undefined;
  }

  function allFieldsIsValid(validated): boolean {
    return Object.keys(validated).every((field) => validated[field].valid);
  }

  const hasErrors = (): boolean => !allFieldsIsValid(resultValidations);
  return {
    validated: resultValidations,
    validateFields,
    addFieldValidation,
    reset,
    hasErrors,
  };
}

function createInicialState(fields): TFieldValidated {
  const inicialState = {};

  for (let field in fields) {
    inicialState[field] = { ...validFieldObject, hasBeenValidated: false };
  }
  return inicialState;
}

function isFunction(fn: any) {
  return typeof fn === "function";
}

export default useValidations;
